<template>
  <div>
    <section
      class="assessment-base__items"
      :class="{
        'assessment-base__items--loading': loading,
      }"
    >
      <div class="columns is-multiline is-centered">
        <div
          v-for="(item, i) in items"
          :key="`result-item-${i}`"
          class="column is-four-quarters-tablet is-one-fourth-desktop"
        >
          <div class="assessment-base-item">
            <div
              :style="{ backgroundImage: `url('${item.icon}')` }"
              class="assessment-base-item__icon"
            />
            <div class="assessment-base-item__content">
              <p class="assessment-base-item__title">
                {{ item.title }}
              </p>
              <p class="assessment-base-item__description" v-html="item.description" />
            </div>
          </div>
        </div>
      </div>
    </section>
    <slot v-if="!loading" name="action" />
    <slot v-if="!loading" name="footer" />
  </div>
</template>

<script>
// Services
import { getPotentialBuyers } from '@/services/apiMapper';
import CONFIG from '@/config/env';

// Icons
import {
  marketDemand1,
  marketDemand2,
  marketDemand3,
  marketDemand4,
  marketDemand5,
  womanRating1,
  womanRating2,
  womanRating3,
  womanRating4,
  womanRating5,
  timer1,
  timer2,
  timer3,
  timer4,
  timer5,
  documentsHandover,
} from 'homeday-assets/L';
import { info } from 'homeday-assets';

// Constants
import { MUTATION, GETTER } from '@/store/CONSTANTS';
import { DEFAULT_OPTIMAL_PRICE_VALUE, OPTIMAL_PRICE_FACTORS as FACTORS } from '@/config/CONSTANTS';

const RESULTS = {
  marketDemand: [
    {
      icon: marketDemand5,
      level: 'HIGH',
    },
    {
      icon: marketDemand4,
      level: 'GOOD',
    },
    {
      icon: marketDemand3,
      level: 'AVERAGE',
    },
    {
      icon: marketDemand2,
      level: 'LOW',
    },
    {
      icon: marketDemand1,
      level: 'VERY_LOW',
    },
  ],
  buyerInterest: [
    {
      icon: womanRating5,
    },
    {
      icon: womanRating4,
    },
    {
      icon: womanRating3,
    },
    {
      icon: womanRating2,
    },
    {
      icon: womanRating1,
    },
  ],
  sellingTime: [
    {
      icon: timer1,
      months: '1-3',
    },
    {
      icon: timer2,
      months: '2-4',
    },
    {
      icon: timer3,
      months: '3-6',
    },
    {
      icon: timer4,
      months: '9-12',
    },
    {
      icon: timer5,
      months: '12-24',
    },
  ],
};

export default {
  name: 'AssessmentValueBase',
  props: {
    rangePrice: {
      type: Number,
      required: true,
    },
    rangePriceIndex: {
      type: Number,
      required: false,
      default: FACTORS.findIndex(({ optimalPrice }) => optimalPrice),
    },
  },
  data() {
    return {
      // We initialize the `potentialBuyers` property with an array of zeros
      potentialBuyers: Array(FACTORS.length).fill(0),
      loading: true,
      optimalPrice: this.getOptimalPrice(),
    };
  },
  computed: {
    rangePriceLevel() {
      return FACTORS[this.rangePriceIndex]?.level || 1;
    },
    items() {
      const items = [this.marketDemand, this.sellingTime];

      if (this.shouldShowBuyerInterest) {
        items.splice(1, 0, this.buyerInterest);
      }

      if (this.showValuation) {
        items.push(this.lendingValueItem);
      }

      return items;
    },
    marketDemand() {
      const currentMarketDemand = RESULTS.marketDemand[this.rangePriceLevel];
      return {
        ...currentMarketDemand,
        title: this.$t(`WIZARD.SELLING_STRATEGY.${currentMarketDemand.level}`),
        description: this.$t('WIZARD.SELLING_STRATEGY.MARKET_DEMAND_DESCRIPTION'),
      };
    },
    buyerInterest() {
      const potentialBuyers = this.potentialBuyers[this.rangePriceLevel];
      const potentialBuyersVerbose = this.$tc(
        'WIZARD.SELLING_STRATEGY.POTENTIAL_BUYER',
        potentialBuyers,
      );
      const description = this.$t('WIZARD.SELLING_STRATEGY.BUYER_INTEREST_DESCRIPTION', {
        potentialBuyers: potentialBuyersVerbose,
      });

      return {
        ...RESULTS.buyerInterest[this.rangePriceLevel],
        title: potentialBuyers,
        description,
      };
    },
    sellingTime() {
      const { months } = RESULTS.sellingTime[this.rangePriceLevel];
      return {
        ...RESULTS.sellingTime[this.rangePriceLevel],
        title: `${months} ${this.$tc('GENERAL.MONTH', 2)}`,
        description: this.$t('WIZARD.SELLING_STRATEGY.SELLING_TIME_DESCRIPTION'),
      };
    },
    lendingValueItem() {
      return {
        icon: documentsHandover,
        title: this.lendingPrice,
        description: this.$t('WIZARD.SELLING_STRATEGY.LENDING_VALUE_MESSAGE'),
      };
    },
    shouldShowBuyerInterest() {
      // We return `false` if we don't have potential buyers
      return !this.potentialBuyers.every((value) => !value);
    },
    shouldShowPriceReductionHint() {
      return CONFIG.feature_flags.edm_questionnaire;
    },
    lendingValue() {
      return this.$store.getters[GETTER.WIZARD.CURRENT_DEAL.LENDING_VALUE] * 1.2;
    },
    lendingPrice() {
      return this.lendingValue ? this.$formatPrice(this.lendingValue) : null;
    },
    icons() {
      return { documentsHandover, info };
    },
    showValuation() {
      return (
        this.lendingValue &&
        this.$store.getters[GETTER.WIZARD.CURRENT_DEAL.MARKETING_PRICE] > this.lendingValue
      );
    },
  },
  async created() {
    const MAX_POTENTIAL_BUYERS = 30;

    try {
      const data = await getPotentialBuyers({
        dealUid: this.$store.state.wizard.currentDeal.uid,
        prices: [this.rangePrice],
      });

      let numberOfPotentialBuyers = data.potential_buyers[0];

      if (numberOfPotentialBuyers > MAX_POTENTIAL_BUYERS) {
        // For business reasons we cap the number of potential buyers here
        // we subtract the first digit from the max to have a random number
        // but unique to the number of BRs we receive
        // to avoid showing different numbers on slide reload
        const firstDigit = numberOfPotentialBuyers % 10;

        numberOfPotentialBuyers = MAX_POTENTIAL_BUYERS - firstDigit;
      }

      this.potentialBuyers = FACTORS.map(({ potentialBuyers: factor }) =>
        Math.round(numberOfPotentialBuyers * factor),
      );
    } catch (error) {
      console.error('getPotentialBuyers error', error);
    }

    this.loading = false;
  },
  methods: {
    getOptimalPrice() {
      if (this.$store.state.wizard.currentDeal.optimalPrice) {
        return this.$store.state.wizard.currentDeal.optimalPrice;
      }

      const optimalPrice =
        this.$store.getters[GETTER.WIZARD.CURRENT_DEAL.MARKETING_PRICE] *
        DEFAULT_OPTIMAL_PRICE_VALUE;
      this.$store.commit(MUTATION.WIZARD.CURRENT_DEAL.SET_OPTIMAL_PRICE, optimalPrice);

      return optimalPrice;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.assessment-base {
  &__items {
    margin-bottom: $sp-l;

    @include for('tablet') {
      margin-bottom: $sp-xl;
    }

    &::before,
    &::after {
      content: '';
      display: block;
      position: absolute;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.3s ease-in-out 0s;
    }

    &::before {
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-color: $white;
      transition-delay: 0.3s;
    }

    &::after {
      $size: 30px;

      top: calc(50% - calc(#{$size} / 2));
      right: calc(50% - calc(#{$size} / 2));
      width: $size;
      height: $size;
      border: 2px solid getShade($secondary-color, 110);
      border-right-color: transparent;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    &--loading {
      &::before,
      &::after {
        transition-delay: 0s;
        opacity: 1;
      }
    }
  }

  &-item {
    display: flex;
    flex: 1;

    @include for('tablet') {
      align-items: center;
    }

    @include for('desktop') {
      flex-direction: column;
      text-align: center;
    }

    &__icon {
      flex-shrink: 0;
      width: 80px;
      height: 80px;
      background-size: 100%;
      transition: background-image 0.3s;

      @include for('tablet') {
        width: 120px;
        height: 120px;
      }
    }

    &__content {
      margin-left: $sp-m;

      @include for('desktop') {
        margin-left: 0;
      }
    }

    &__title {
      @include font('DS-200');
      font-weight: 700;

      @include for('tablet') {
        @include font('DS-400');
      }
    }

    &__description {
      word-break: break-word;
      hyphens: manual;
      @include font('DS-80');

      @include for('tablet') {
        @include font('DS-100');
      }
    }
  }

  &__footer {
    display: flex;
    align-items: flex-end;
  }

  &__hint {
    max-width: 700px;
    margin: $sp-xl auto 0;
    padding: $sp-l;
    display: flex;
    background-color: getShade($secondary-color, 70);
    border-radius: 8px;
  }
}
</style>
